import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";

import LeadDialog from "./LeadDialog";
import DataGridTable from "./DataGrid";
import LeadScoreBar from "./LeadScoreBar";

import { getRequestData } from "../Utility/apiHelper";
import {
    formatSourceProductDates,
    handleValidateCodevalue,
    productRecommendationColumnWidths
} from "../Utility/constant";
import InfoIcon from "./Widgets/InfoIcon";
import { ToggleContainer } from "./Styled";
import "./ProductRecommendationDialog.css";
import { requestShowDialogLoader,requestHideDialogLoader } from "../Store/Global/action";

function ProductRecommendationDialog({
    addedProducts = [],
    open,
    onClose,
    url,
    title,
    primaryButtonLabel,
    secondaryButtonLabel,
    onPrimaryAction,
    onSecondaryAction,
    showSourceProducts = true,
    showToggle = true,
    size = "lg",
    infoText = "PROD_RECC_INFO"
}) {

    const { enqueueSnackbar } = useSnackbar();

    const internationalise = useSelector(
        state => state?.internationalise?.currentHashMapData
    );
    const dispatch = useDispatch();
    const [recommendations, setRecommendations] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);
    const [filterAddedProducts , setFilterAddedProducts] = useState(false);
    useEffect(() => {
        if (!open || !url) return;

        const fetchRecommendations = async () => {
            try {
                dispatch(requestShowDialogLoader());
                setRecommendations([]);

                const response = await getRequestData(url);

                setRecommendations(response?.data || []);
            } catch (err) {
                enqueueSnackbar(
                    err?.response?.data?.message ||
                    { variant: "error" }
                );
            } finally {
                dispatch(requestHideDialogLoader());
            }
        };

        fetchRecommendations();
    }, [open, url]);

const handleDialogClose = useCallback(() => {
    setSelectionModel([]);
    onClose?.();
}, [onClose]);

const rows = useMemo(() => {
    const addedProductIds = new Set(
        addedProducts.map(product => product.productId ?? product.id)
    );
    const getRecProductId = (rec) => rec.productId ?? rec.recommendedProductId;

    const filteredRecommendations = !filterAddedProducts
        ? recommendations.filter(
              rec => !addedProductIds.has(getRecProductId(rec))
          )
        : recommendations;

    return filteredRecommendations.map((rec, index) => ({
        ...rec,
        id: getRecProductId(rec) ?? rec.id ?? index,
    }));
}, [recommendations, filterAddedProducts, addedProducts]
    );

    const RECOMMENDATIONS_COLUMNS = useMemo(
    () => [
        {
            field: "recommendedProductName",
            headerName: handleValidateCodevalue(
                internationalise,
                "PROD_RECC_NAME"
            ),
            flex: 1,
            minWidth: productRecommendationColumnWidths.productName,
            valueGetter: (params) => params.row?.productName || params.row?.recommendedProductName || ""
        },
        {
            field: "averageProbability",
            headerName: handleValidateCodevalue(
                internationalise,
                "PROD_RECC_SCORE"
            ),
            flex: 1,
            width: productRecommendationColumnWidths.probability,
            renderCell: params => {
                const raw = params.row?.score ?? params.row?.averageProbability;
                if (raw == null) return "-";
                const num = Number(raw);
                const scoreVal = num <= 1 ? Math.round(num * 100) : Math.round(num);
                return <LeadScoreBar score={scoreVal} />;
            }
        },
        ...(showSourceProducts ? [
            {
                field: "sourceProductsLastBoughtDate",
                headerName: handleValidateCodevalue(
                    internationalise,
                    "PROD_RECC_SOURCE_PROD_LPD"
                ),
                flex: 1.5,
                minWidth: productRecommendationColumnWidths.lastBoughtDate,
                renderCell: params => {
                    const parts = formatSourceProductDates(params.row.sourceProducts);

                    return (
                        <Box className="prd-source-product-container">
                            {parts.length ? (
                                parts.map((part, idx) => (
                                    <Typography key={idx} variant="body2">
                                        {idx + 1}. {part.name}{" "}
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            className="prd-source-product-date"
                                        >
                                            - {part.date}
                                        </Typography>
                                    </Typography>
                                ))
                            ) : (
                                <Typography variant="body2">—</Typography>
                            )}
                        </Box>
                    );
                }
            }
        ] : [])
    ],
    [internationalise, showSourceProducts]
)

    return (
        <LeadDialog
            open={open}
            handleClose={handleDialogClose}
            title={title}
            size={size}
            checked
            selectionModel={selectionModel}
            btnlabel={primaryButtonLabel}
            btnlabel2={secondaryButtonLabel}
            handleSave={() => {
                onPrimaryAction?.(selectionModel);
                handleDialogClose();
            }}
            handleSave2={() => {
                onSecondaryAction?.(selectionModel);
                handleDialogClose();
            }}
        >

            <InfoIcon
                text = {infoText}
            /> 

        {showToggle && (
            <ToggleContainer>
                <FormControlLabel
                control={
                    <Switch
                        checked={filterAddedProducts}
                        onChange={()=> setFilterAddedProducts(!filterAddedProducts)}
                        name="filterAddedProducts"
                        color="primary"
                    />
                }
                label={handleValidateCodevalue(internationalise, "SHOW_PURCHASED_PRODUCTS")}
            />
            </ToggleContainer>
        )}
                <DataGridTable
                    columns={RECOMMENDATIONS_COLUMNS}
                    dataGridData={rows}
                    getRowHeight={(params) => {
                        if (!showSourceProducts) return 52;
                        const lines = Math.max(params?.model?.sourceProducts?.length || 1, 1);

                        return 45 + (lines - 1) * 20;
                    }}
                    height="calc(100vh - 365px)"
                    isRequiredToolBar={false}
                    isCheckboxReq
                    selectionModel={selectionModel}
                    setSelectionModel={setSelectionModel}
                />
        </LeadDialog>
    );
}

export default React.memo(ProductRecommendationDialog);